import { useTranslation } from 'react-i18next';
import i18nInstance from '@/lib/i18n/client';

// Define the structure of our translation keys
type TranslationKeys = {
  navbar: {
    help: string;
    guide: string;
    faqs: string;
    login: string;
    signUp: string;
    contactSales: string;
    forSpanishMarket: string;
    forItalianMarket: string;
    spanishMarket: string;
    italianMarket: string;
    forTravelers: string;
    forMerchants: string;
    sponsorsAndAwards: string;
  };
  merchants: {
    hero: {
      heading: string;
      description: string;
      ctaText: string;
      ctaLink: string;
      imageAlt: string;
    };
    sponsors: {
      heading: string;
      investment: {
        title: string;
      };
      support: {
        title: string;
        text: string;
      };
      blockchain: {
        title: string;
        text: string;
      };
      accelerators: {
        title: string;
      };
      awards: {
        title: string;
      };
    };
  };
  landing: {
    title: string;
    description: string;
  };
  travelersHero: {
    heading: string;
    description: string;
    ctaHeading: string;
    ctaButton: string;
  };
  optionsSection: {
    title: string;
    shop: {
      title: string;
      description: string;
    };
    validate: {
      title: string;
      description: string;
    };
    save: {
      title: string;
      description: string;
    };
  };
  whySection: {
    title: string;
    subtitle: string;
    realTaxFree: {
      title: string;
      description: string;
    };
    fullyCompliant: {
      title: string;
      description: string;
    };
    instantInvoice: {
      title: string;
      description: string;
    };
    fastCheckout: {
      title: string;
      description: string;
    };
    simpleValidation: {
      title: string;
      description: string;
    };
  };
  toolsSection: {
    title: string;
    subtitle: string;
    wechat: {
      title: string;
      description: string;
      button: string;
    };
    linepass: {
      title: string;
      description: string;
      button: string;
    };
    travelerApp: {
      title: string;
      description: string;
      iosButton: string;
      androidButton: string;
    };
  };
  testimonials: {
    title: string;
    testimonial1: {
      name: string;
      role: string;
      text: string;
    };
    testimonial2: {
      name: string;
      role: string;
      text: string;
    };
    testimonial3: {
      name: string;
      role: string;
      text: string;
    };
    testimonial4: {
      name: string;
      role: string;
      text: string;
    };
  };
  notFound: {
    title: string;
    message: string;
    homeButton: string;
  };
  feeCalculatorSection: {
    title: string;
    hero: string;
    description: string;
    sliderLabel: string;
    feeLabel: string;
    staticFee: string;
    vatNote: string;
    cta: string;
    flagItaly: string;
    flagSpain: string;
  };
};

// Create a typed version of the t function
type TypedT = {
  (key: keyof TranslationKeys | `${'navbar' | 'merchants' | 'landing' | 'travelersHero' | 'optionsSection' | 'whySection' | 'toolsSection' | 'testimonials' | 'notFound' | 'feeCalculatorSection'}.${string}`, options?: Record<string, unknown>): string;
};

export function useTypedTranslation() {
  const { t, ...rest } = useTranslation(undefined, { i18n: i18nInstance });
  
  // Cast the t function to our typed version
  return {
    t: t as unknown as TypedT,
    ...rest
  };
}
